import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import type { A2PRegistrationInfo, A2PRegistrationStatus } from '@vendasta/protos/sms/v2/types';
import { A2P_START_DATE, RegistrationStage, VENDASTA_OWNED_CAMPAIGN_FIELDS } from './a2p-registration.constants';

export function isVendastaOwnedField(field: string): boolean {
  const normalized = field.toLowerCase();
  return (
    VENDASTA_OWNED_CAMPAIGN_FIELDS.has(normalized) ||
    normalized.startsWith('sample_message') ||
    normalized.startsWith('message_sample')
  );
}

export function hasRegistrationError(status: A2PRegistrationStatus | undefined): boolean {
  if (!status) {
    return false;
  }

  const isBusinessProfileNonCompliant =
    status.secondaryBusinessProfile?.status === 'VALIDATION_STATUS_NON_COMPLIANT';
  const isTrustBundleNonCompliant = status.trustBundle?.status === 'VALIDATION_STATUS_NON_COMPLIANT';
  const isBrandFailed = status.brand?.status === 'BRAND_STATUS_FAILED';
  const isCampaignFailed = status.messagingCampaign?.status === 'MESSAGING_CAMPAIGN_STATUS_FAILED';

  return isBusinessProfileNonCompliant || isTrustBundleNonCompliant || isBrandFailed || isCampaignFailed;
}

// Max time we optimistically hide a prior error while waiting for a re-submission's new
// status row to be written. A genuine resubmit produces a new status within minutes; past
// this window we treat the submission as stuck/stale and surface the real status instead of
// masking it as "In Progress" forever.
const AWAITING_STATUS_GRACE_MS = 60 * 60 * 1000;

export function isAwaitingStatusCreation(
  status: A2PRegistrationStatus | undefined,
  info: A2PRegistrationInfo | undefined,
): boolean {
  if (!info?.submitted) {
    return false;
  }

  const submittedRecently = Date.now() - info.submitted.getTime() < AWAITING_STATUS_GRACE_MS;

  if (!status) {
    return info.submitted > A2P_START_DATE;
  }

  if (hasRegistrationError(status) && status.updated) {
    return info.submitted > status.updated && submittedRecently;
  }

  return false;
}

export function getRegistrationStage(
  a2pRegistrationStatus: A2PRegistrationStatus | undefined,
  a2pRegistrationInfo: A2PRegistrationInfo | undefined,
): RegistrationStage {
  if (!a2pRegistrationInfo || !a2pRegistrationStatus) {
    return RegistrationStage.NotSubmitted;
  }
  const isSubmitted = !!a2pRegistrationInfo?.submitted && a2pRegistrationInfo.submitted > A2P_START_DATE;

  if (!isSubmitted) {
    return RegistrationStage.NotSubmitted;
  }

  if (isAwaitingStatusCreation(a2pRegistrationStatus, a2pRegistrationInfo)) {
    return RegistrationStage.ReviewingInfo;
  }

  const secondaryBusinessProfileStatus =
    a2pRegistrationStatus.secondaryBusinessProfile?.status ?? 'VALIDATION_STATUS_UNSPECIFIED';
  const trustBundleStatus = a2pRegistrationStatus.trustBundle?.status ?? 'VALIDATION_STATUS_UNSPECIFIED';
  const brandStatus = a2pRegistrationStatus.brand?.status ?? 'BRAND_STATUS_UNSPECIFIED';
  const messagingCampaignStatus =
    a2pRegistrationStatus.messagingCampaign?.status ?? 'MESSAGING_CAMPAIGN_STATUS_UNSPECIFIED';

  if (
    secondaryBusinessProfileStatus === 'VALIDATION_STATUS_NON_COMPLIANT' ||
    trustBundleStatus === 'VALIDATION_STATUS_NON_COMPLIANT' ||
    brandStatus === 'BRAND_STATUS_FAILED'
  ) {
    return RegistrationStage.DataError;
  }

  if (messagingCampaignStatus === 'MESSAGING_CAMPAIGN_STATUS_FAILED') {
    return RegistrationStage.CampaignError;
  }

  if (messagingCampaignStatus === 'MESSAGING_CAMPAIGN_STATUS_VERIFIED') {
    return RegistrationStage.RegistrationComplete;
  }

  return RegistrationStage.ReviewingInfo;
}

export function createGroupValueChangeValidator(
  groupOfFields: string[],
  initialValues: Map<string, string>,
  getAllControls: () => Record<string, FormControl>,
): ValidatorFn {
  const originalValuesMap = new Map<string, string>();
  groupOfFields.forEach((fieldName) => {
    const val = initialValues.get(fieldName);
    if (val !== undefined) {
      originalValuesMap.set(fieldName, val);
    }
  });

  return (): ValidationErrors | null => {
    const controls = getAllControls();
    for (const [fieldName, originalValue] of originalValuesMap.entries()) {
      const c = controls[fieldName];
      if (c !== undefined && c.value !== originalValue) {
        return null;
      }
    }
    return { groupValueError: true };
  };
}
